import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Card, 
  FormControlLabel,
  Grid, 
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { connect } from 'react-redux';
import { fetchFinancialAccounts, postAccountBalance, postNetWorthSnapshot, fetchNetWorthSnapshots } from '../redux/actions';
import NetWorthChart from './NetWorthChart';
import NetWorthChartDetail from './NetWorthChartDetail';
import '../style/NTWRTH.css';

const HOUSE_EQUITY_ACCOUNT_ID = 3;

class NTWRTH extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      balanceInputs: {},
      showNetWorthChartDetail: false,
      excludeHouseEquity: false
    };
    this.props.dispatch(fetchFinancialAccounts());
    this.props.dispatch(fetchNetWorthSnapshots());
  }

  calculateNetWorth = () => {
    const accounts = this.props.financialAccounts;
    if (!accounts || accounts.length === 0) {
      return 0;
    }
    const total = accounts.reduce((sum, account) => {
      return sum + (Number(account.balance) || 0);
    }, 0);
    if (!this.state.excludeHouseEquity) {
      return total;
    }
    return total - this.getHouseEquityBalance();
  }

  calculateSnapshotAmount = () => {
    const accounts = this.props.financialAccounts;
    if (!accounts || accounts.length === 0) {
      return 0;
    }
    return accounts.reduce((sum, account) => {
      return sum + (Number(account.balance) || 0);
    }, 0);
  }

  getHouseEquityBalance = () => {
    const accounts = this.props.financialAccounts || [];
    const house = accounts.find(account => Number(account.id) === HOUSE_EQUITY_ACCOUNT_ID);
    return house ? (Number(house.balance) || 0) : 0;
  }

  handleBalanceInputChange = (accountId, value) => {
    this.setState(prevState => ({
      balanceInputs: {
        ...prevState.balanceInputs,
        [accountId]: value
      }
    }));
  }

  handleSubmitBalance = (accountId) => {
    const amount = parseFloat(this.state.balanceInputs[accountId]);
    if (isNaN(amount)) {
      alert('Please enter a valid number');
      return;
    }
    this.props.dispatch(postAccountBalance(accountId, amount))
      .then(() => {
        alert('Balance updated successfully!');
        // Clear the input after successful submission
        this.setState(prevState => ({
          balanceInputs: {
            ...prevState.balanceInputs,
            [accountId]: ''
          }
        }));
      })
      .catch((error) => {
        alert('Failed to update balance. Please try again.');
      });
  }

  handleSubmitNetWorth = () => {
    const netWorth = this.calculateSnapshotAmount();
    this.props.dispatch(postNetWorthSnapshot(netWorth))
      .then(() => {
        alert('Net worth snapshot submitted successfully!');
      })
      .catch((error) => {
        alert('Failed to submit net worth snapshot. Please try again.');
      });
  }

  handleCheckBalance = (url) => {
    if (url && url.trim() !== '') {
      window.open(url, '_blank');
    }
  }

  formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Denver'
    });
  }

  getMostRecentSnapshotDate = () => {
    const snapshots = this.props.netWorthSnapshots || [];
    if (snapshots.length === 0) {
      return null;
    }
    // Snapshots are already sorted by created_at descending from API
    const mostRecent = snapshots[0];
    return mostRecent.created_at;
  }

  render() {
    const netWorth = this.calculateNetWorth();
    const accounts = (this.props.financialAccounts || []).sort((a, b) => {
      const balanceA = Math.abs(a.balance || 0);
      const balanceB = Math.abs(b.balance || 0);
      return balanceB - balanceA; // Descending order (highest first)
    });

    return (
      <div className="NTWRTH-container">
        {/* Net Worth Display */}
        <Card raised className="NTWRTH-net-worth-card" elevation={3}>
          <Typography variant="h4" component="h2" className="NTWRTH-net-worth-label">
            Net Worth
          </Typography>
          <Typography variant="h3" component="div" className="NTWRTH-net-worth-amount">
            {this.formatCurrency(netWorth)}
          </Typography>

          <FormControlLabel
            className="NTWRTH-exclude-house"
            control={
              <Switch
                checked={this.state.excludeHouseEquity}
                onChange={(e) => this.setState({ excludeHouseEquity: e.target.checked })}
                color="default"
                inputProps={{ 'aria-label': 'Exclude house equity' }}
              />
            }
            label="Exclude house equity"
          />
          
          {/* Net Worth Snapshot Button */}
          <div className="NTWRTH-net-worth-input-container">
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleSubmitNetWorth}
            >
              Submit Net Worth Snapshot
            </Button>
            {this.getMostRecentSnapshotDate() && (
              <Typography 
                variant="caption" 
                className="NTWRTH-last-snapshot-date"
              >
                Last: {this.formatDate(this.getMostRecentSnapshotDate())}
              </Typography>
            )}
          </div>

          <Link to="/ntwrth/projection" className="NTWRTH-projection-link">
            Project future net worth →
          </Link>
        </Card>

        {/* Accounts List */}
        <div className="NTWRTH-accounts-container">
          <Typography variant="h5" component="h3" style={{ marginBottom: '15px', fontSize: '1.25rem' }}>
            Accounts
          </Typography>
          
          {accounts.length === 0 ? (
            <Typography>No accounts found. Loading...</Typography>
          ) : (
            <Grid container spacing={2} style={{ margin: 0, width: '100%' }}>
              {accounts.map(account => (
                <Grid item xs={12} key={account.id}>
                  <Card raised className="NTWRTH-account-card" elevation={2}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <Typography variant="h6" component="div" className="NTWRTH-account-name" style={{ flex: 1 }}>
                        {account.name}
                      </Typography>
                      {/* Check Balance Button */}
                      {account.url && account.url.trim() !== '' && (
                        <Button 
                          variant="contained" 
                          onClick={() => this.handleCheckBalance(account.url)}
                          className="NTWRTH-check-balance-button"
                        >
                          Check
                        </Button>
                      )}
                    </div>
                    <Typography variant="body1" className="NTWRTH-account-balance">
                      Balance: {this.formatCurrency(account.balance)}
                    </Typography>
                    {account.balance_updated_at && (
                      <Typography variant="caption" className="NTWRTH-account-updated">
                        Updated: {this.formatDate(account.balance_updated_at)}
                      </Typography>
                    )}
                    
                    {/* Balance Input */}
                    <div className="NTWRTH-balance-input-container">
                      <TextField
                        label="New Balance"
                        type="number"
                        value={this.state.balanceInputs[account.id] || ''}
                        onChange={(e) => this.handleBalanceInputChange(account.id, e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        style={{ marginBottom: '10px' }}
                      />
                      <Button 
                        variant="contained" 
                        color="secondary" 
                        size="small"
                        onClick={() => this.handleSubmitBalance(account.id)}
                        fullWidth
                      >
                        Update Balance
                      </Button>
                    </div>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </div>

        {/* Net Worth summary chart — tap opens detailed chart overlay */}
        {(this.props.netWorthSnapshots || []).length >= 2 && (
          <div
            className="NTWRTH-chart-launcher"
            onClick={() => this.setState({ showNetWorthChartDetail: true })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.setState({ showNetWorthChartDetail: true });
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Open detailed net worth chart"
          >
            <NetWorthChart
              snapshots={this.props.netWorthSnapshots}
              excludeHouseEquity={this.state.excludeHouseEquity}
            />
            <Typography variant="caption" className="NTWRTH-chart-launcher-hint">
              Tap for detailed chart
            </Typography>
          </div>
        )}

        {this.state.showNetWorthChartDetail && (
          <NetWorthChartDetail
            snapshots={this.props.netWorthSnapshots}
            excludeHouseEquity={this.state.excludeHouseEquity}
            onClose={() => this.setState({ showNetWorthChartDetail: false })}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    financialAccounts: state.reducer.financialAccounts,
    netWorthSnapshots: state.reducer.netWorthSnapshots
  }
}

export default connect(mapStateToProps)(NTWRTH);

